import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cog,
  FileText,
  Search,
  Sparkles,
  User,
} from "@/components/icons"
import { cn } from "@/lib/utils"

/**
 * Readable renderer for the `session_context` payload returned by the
 * admin hydrate endpoint. Falls back to raw JSON if the shape doesn't
 * match what we expect — we don't want the admin view to go blank when
 * the chat message schema evolves.
 */
type TranscriptPayload = {
  message_count?: number
  recent_messages?: TranscriptMessage[]
}

type TranscriptMessage = {
  user?: string
  assistant?: {
    content?: string
    error?: unknown
    error_id?: string | null
    completed?: boolean
    activities?: Activity[] | null
    document_references?: Array<string | { title?: string; id?: string }> | null
    user_files?: Array<string | { name?: string }> | null
    assistant_files?: Array<string | { name?: string }> | null
  }
}

type Activity = {
  activity_type?: string
  message?: string
  timestamp?: number
  details?: Record<string, unknown>
}

export function TranscriptView({ value }: { value: unknown }) {
  const { t } = useTranslation()
  const payload = value as TranscriptPayload | null
  const messages = payload?.recent_messages ?? []

  if (!Array.isArray(messages) || messages.length === 0) {
    // Schema drift / truly empty — fall back to a JSON dump so admins can
    // still eyeball whatever did come back.
    return (
      <pre className="bg-muted/40 max-h-96 overflow-auto rounded-md border p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.feedback.detail.transcriptCount", {
          defaultValue: "Showing {{count}} recent message",
          defaultValue_other: "Showing {{count}} recent messages",
          count: messages.length,
        })}
      </p>
      <div className="space-y-6">
        {messages.map((msg, idx) => (
          <MessagePair key={idx} message={msg} />
        ))}
      </div>
    </div>
  )
}

function MessagePair({ message }: { message: TranscriptMessage }) {
  const { t } = useTranslation()
  const assistant = message.assistant ?? {}
  const activities = Array.isArray(assistant.activities) ? assistant.activities : []
  const docRefs = Array.isArray(assistant.document_references) ? assistant.document_references : []
  const userFiles = Array.isArray(assistant.user_files) ? assistant.user_files : []
  const assistantFiles = Array.isArray(assistant.assistant_files) ? assistant.assistant_files : []

  return (
    <div className="border-border overflow-hidden rounded-md border">
      {message.user ? (
        <Bubble author="user" icon={<User className="size-4" />}>
          {message.user}
        </Bubble>
      ) : null}

      <Bubble
        author="assistant"
        icon={<Bot className="size-4" />}
        statusBadge={
          <>
            {assistant.completed ? (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-[10px]">
                <CheckCircle2 className="text-success size-3" />
                {t("adminDashboard.feedback.detail.completed", "Completed")}
              </span>
            ) : null}
            {assistant.error ? (
              <span className="bg-destructive/10 text-destructive rounded px-1.5 py-0.5 text-[10px] font-medium">
                {t("adminDashboard.feedback.detail.errored", "Errored")}
              </span>
            ) : null}
          </>
        }
      >
        {assistant.content ? (
          <div className="whitespace-pre-wrap">{assistant.content}</div>
        ) : (
          <em className="text-muted-foreground">
            {t("adminDashboard.feedback.detail.noAssistantContent", "No assistant reply.")}
          </em>
        )}
      </Bubble>

      {activities.length > 0 ? <ActivitiesBlock activities={activities} /> : null}

      {(userFiles.length > 0 || assistantFiles.length > 0 || docRefs.length > 0) && (
        <AttachmentsBlock assistantFiles={assistantFiles} docRefs={docRefs} userFiles={userFiles} />
      )}
    </div>
  )
}

function Bubble({
  author,
  icon,
  children,
  statusBadge,
}: {
  author: "user" | "assistant"
  icon: React.ReactNode
  children: React.ReactNode
  statusBadge?: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        "border-border border-b px-3 py-2 text-xs last:border-0",
        author === "user" ? "bg-muted/40" : "bg-background"
      )}
    >
      <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-[10px] font-semibold tracking-wide uppercase">
        {icon}
        {author === "user"
          ? t("adminDashboard.feedback.detail.roleUser", "User")
          : t("adminDashboard.feedback.detail.roleAssistant", "Assistant")}
        {statusBadge ? (
          <span className="ml-auto flex items-center gap-2">{statusBadge}</span>
        ) : null}
      </div>
      <div className="text-foreground">{children}</div>
    </div>
  )
}

function ActivitiesBlock({ activities }: { activities: Activity[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <div className="border-border bg-muted/20 border-t">
      <button
        className="hover:bg-accent/40 flex w-full items-center gap-2 px-3 py-2 text-[11px] font-medium"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        <Sparkles className="size-3" />
        {t("adminDashboard.feedback.detail.activities", "Agent activity")}
        <span className="text-muted-foreground">({activities.length})</span>
      </button>
      {open ? (
        <ol className="divide-border divide-y px-3 pb-2 text-xs">
          {activities.map((act, idx) => (
            <ActivityRow key={idx} activity={act} />
          ))}
        </ol>
      ) : null}
    </div>
  )
}

function ActivityRow({ activity }: { activity: Activity }) {
  const [expanded, setExpanded] = useState(false)
  const type = activity.activity_type ?? "activity"
  const Icon = iconForActivity(type)
  const hasDetails = activity.details && Object.keys(activity.details).length > 0

  return (
    <li className="py-2">
      <div className="flex items-start gap-2">
        <Icon className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-muted-foreground font-mono text-[10px] uppercase">{type}</span>
            {typeof activity.timestamp === "number" ? (
              <span className="text-muted-foreground text-[10px]">
                {formatTimestamp(activity.timestamp)}
              </span>
            ) : null}
          </div>
          {activity.message ? (
            <p className="line-clamp-3 whitespace-pre-wrap">{activity.message}</p>
          ) : null}
          {hasDetails ? (
            <ActivityDetails
              details={activity.details ?? {}}
              expanded={expanded}
              onToggle={() => setExpanded((v) => !v)}
            />
          ) : null}
        </div>
      </div>
    </li>
  )
}

function ActivityDetails({
  details,
  expanded,
  onToggle,
}: {
  details: Record<string, unknown>
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  // Search results — render as a compact list of titles.
  if (Array.isArray(details.results)) {
    const results = details.results as Array<{ title?: string; library?: string; id?: string }>
    return (
      <div className="mt-1">
        <button
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
          type="button"
          onClick={onToggle}
        >
          {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          {t("adminDashboard.feedback.detail.resultsCount", {
            defaultValue: "{{count}} result",
            defaultValue_other: "{{count}} results",
            count: results.length,
          })}
        </button>
        {expanded ? (
          <ul className="text-muted-foreground mt-1 space-y-0.5 text-[11px]">
            {results.map((r, i) => (
              <li key={i} className="truncate">
                · {r.title ?? r.id ?? JSON.stringify(r)}
                {r.library ? (
                  <span className="text-muted-foreground/70 ml-1">— {r.library}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    )
  }

  // Reasoning / summary text — collapsible, rendered as preformatted.
  const summary = typeof details.summary_text === "string" ? details.summary_text : null
  if (summary) {
    return (
      <div className="mt-1">
        <button
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
          type="button"
          onClick={onToggle}
        >
          {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          {t("adminDashboard.feedback.detail.reasoning", "Show reasoning")}
        </button>
        {expanded ? (
          <p className="text-muted-foreground mt-1 text-[11px] whitespace-pre-wrap">{summary}</p>
        ) : null}
      </div>
    )
  }

  // Everything else — tool call ids, translation keys, etc. — just a raw block.
  return (
    <div className="mt-1">
      <button
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
        type="button"
        onClick={onToggle}
      >
        {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {t("adminDashboard.feedback.detail.details", "Details")}
      </button>
      {expanded ? (
        <pre className="bg-muted/60 text-muted-foreground mt-1 overflow-auto rounded p-2 text-[10px]">
          {JSON.stringify(details, null, 2)}
        </pre>
      ) : null}
    </div>
  )
}

function AttachmentsBlock({
  userFiles,
  assistantFiles,
  docRefs,
}: {
  userFiles: Array<string | { name?: string }>
  assistantFiles: Array<string | { name?: string }>
  docRefs: Array<string | { title?: string; id?: string }>
}) {
  const { t } = useTranslation()
  return (
    <div className="border-border bg-muted/10 space-y-1.5 border-t px-3 py-2 text-[11px]">
      {docRefs.length > 0 ? (
        <AttachmentList
          icon={<FileText className="size-3" />}
          items={docRefs.map((d) => (typeof d === "string" ? d : (d.title ?? d.id ?? "—")))}
          label={t("adminDashboard.feedback.detail.docRefs", "Document references")}
        />
      ) : null}
      {userFiles.length > 0 ? (
        <AttachmentList
          icon={<FileText className="size-3" />}
          items={userFiles.map((f) => (typeof f === "string" ? f : (f.name ?? "—")))}
          label={t("adminDashboard.feedback.detail.userFiles", "User files")}
        />
      ) : null}
      {assistantFiles.length > 0 ? (
        <AttachmentList
          icon={<FileText className="size-3" />}
          items={assistantFiles.map((f) => (typeof f === "string" ? f : (f.name ?? "—")))}
          label={t("adminDashboard.feedback.detail.assistantFiles", "Assistant files")}
        />
      ) : null}
    </div>
  )
}

function AttachmentList({
  icon,
  label,
  items,
}: {
  icon: React.ReactNode
  label: string
  items: string[]
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground inline-flex items-center gap-1 font-medium">
        {icon}
        {label}:
      </span>
      {items.map((it, i) => (
        <span
          key={i}
          className="border-border bg-background inline-flex max-w-full items-center rounded border px-1.5 py-0.5"
        >
          <span className="truncate">{it}</span>
        </span>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function iconForActivity(type: string) {
  switch (type) {
    case "tools":
    case "tool":
      return Cog
    case "search_results":
    case "search":
      return Search
    case "thinking":
    case "message":
      return Sparkles
    default:
      return ChevronRight
  }
}

function formatTimestamp(epochSeconds: number): string {
  try {
    return new Date(epochSeconds * 1000).toLocaleTimeString()
  } catch {
    return String(epochSeconds)
  }
}
